import { MeasureAggregation } from 'cadenza/repository/model/measure-aggregation';
import { objecttypeIdFromString, objecttypeIdToString } from 'cadenza/repository/model/objecttype-id';
import type { ObjecttypeName } from 'cadenza/repository/model/objecttype-name';
import type { RepositoryName } from 'cadenza/repository/model/repository-name';
import { getStore } from 'cadenza/store/cadenza-store';
import { assertUnreachable } from 'cadenza/utils/custom-error';
import type { MeasuresFromSpatialIntersectionCalculationDto } from 'cadenza/workbook/api/enrichment-api';
import type { EnrichmentType } from 'cadenza/workbook/api/enrichment-type';
import { createAttributeIdFromParams } from 'cadenza/workbook/enrichment/enrichment-utils';
import type { EnrichmentDto, SpecificEnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import type { MeasuresFromSpatialIntersectionCalculation } from 'cadenza/workbook/enrichment/model/enrichment-parametrization-dto';
import { createAttributeId } from 'cadenza/workbook/model/attribute-id-util';
import { getWorkbookRepositoryName } from 'cadenza/workbook/workbook.state';

type OptionsByEnrichmentType<T extends EnrichmentType> =
  T extends 'StructuredGeocodingEnrichment' ? ReturnType<typeof createStructuredGeocodingOptions>
  : T extends 'UnstructuredGeocodingEnrichment' ? ReturnType<typeof createUnstructuredGeocodingOptions>
  : T extends 'ReverseGeocodingEnrichment' ? ReturnType<typeof createReverseGeocodingOptions>
  : T extends 'IpGeocodingEnrichment' ? ReturnType<typeof createIpGeocodingOptions>
  : T extends 'IsochronesEnrichment' ? ReturnType<typeof createIsochronesEnrichmentOptions>
  : T extends 'AnalyticsExtensionEnrichment' ? ReturnType<typeof createAnalyticsExtensionOptions>
  : T extends 'MeasuresFromSpatialIntersectionEnrichment' ?
    ReturnType<typeof createMeasureFromSpatialIntersectionOptions>
  : never;

export function createEnrichmentOptionsFromDtoParametrization<T extends EnrichmentType>(
  enrichmentDto: SpecificEnrichmentDto<T>,
): OptionsByEnrichmentType<T>;
export function createEnrichmentOptionsFromDtoParametrization(enrichmentDto: EnrichmentDto) {
  switch (enrichmentDto.enrichmentType) {
    case 'StructuredGeocodingEnrichment':
      return createStructuredGeocodingOptions(enrichmentDto);
    case 'UnstructuredGeocodingEnrichment':
      return createUnstructuredGeocodingOptions(enrichmentDto);
    case 'IpGeocodingEnrichment':
      return createIpGeocodingOptions(enrichmentDto);
    case 'ReverseGeocodingEnrichment':
      return createReverseGeocodingOptions(enrichmentDto);
    case 'MeasuresFromSpatialIntersectionEnrichment':
      return createMeasureFromSpatialIntersectionOptions(enrichmentDto);
    case 'AnalyticsExtensionEnrichment':
      return createAnalyticsExtensionOptions(enrichmentDto);
    case 'IsochronesEnrichment':
      return createIsochronesEnrichmentOptions(enrichmentDto);
    default:
      assertUnreachable(enrichmentDto);
  }
}

function createStructuredGeocodingOptions(enrichmentDto: SpecificEnrichmentDto<'StructuredGeocodingEnrichment'>) {
  const params = enrichmentDto.parametrization;
  const countryCodeAttribute = createAttributeIdFromParams(
    'countryCodeObjecttypeName',
    'countryCodeAttribute',
    params,
    enrichmentDto,
  );
  const countrySubdivisionAttribute = createAttributeIdFromParams(
    'countrySubdivisionObjecttypeName',
    'countrySubdivisionAttribute',
    params,
    enrichmentDto,
  );
  const countrySecondarySubdivisionAttribute = createAttributeIdFromParams(
    'countrySecondarySubdivisionObjecttypeName',
    'countrySecondarySubdivisionAttribute',
    params,
    enrichmentDto,
  );
  const postalCodeAttribute = createAttributeIdFromParams(
    'postalCodeObjecttypeName',
    'postalCodeAttribute',
    params,
    enrichmentDto,
  );
  const municipalityAttribute = createAttributeIdFromParams(
    'municipalityObjecttypeName',
    'municipalityAttribute',
    params,
    enrichmentDto,
  );
  const settlementAttribute = createAttributeIdFromParams(
    'settlementObjecttypeName',
    'settlementAttribute',
    params,
    enrichmentDto,
  );
  const streetAttribute = createAttributeIdFromParams('streetObjecttypeName', 'streetAttribute', params, enrichmentDto);
  const housenumberAttribute = createAttributeIdFromParams(
    'housenumberObjecttypeName',
    'housenumberSuffixAttribute',
    params,
    enrichmentDto,
  );
  const housenumberSuffixAttribute = createAttributeIdFromParams(
    'housenumberSuffixObjecttypeName',
    'housenumberSuffixAttribute',
    params,
    enrichmentDto,
  );
  return {
    countryCodeAttribute,
    countrySubdivisionAttribute,
    countrySecondarySubdivisionAttribute,
    postalCodeAttribute,
    municipalityAttribute,
    settlementAttribute,
    streetAttribute,
    housenumberAttribute,
    housenumberSuffixAttribute,
  };
}

function createUnstructuredGeocodingOptions(enrichmentDto: SpecificEnrichmentDto<'UnstructuredGeocodingEnrichment'>) {
  const params = enrichmentDto.parametrization;
  const unstructuredAddressAttribute = createAttributeIdFromParams(
    'unstructuredAddressObjecttypeName',
    'unstructuredAddressAttributeName',
    params,
    enrichmentDto,
  );
  const addressContext = params.context;
  const worldBoxSrs = params.mapSrs;
  const worldBoxEnvelope = params.restrictingMapExtent;

  return {
    unstructuredAddressAttribute,
    addressContext,
    worldBoxSrs,
    worldBoxEnvelope,
  };
}

function createIsochronesEnrichmentOptions(enrichmentDto: SpecificEnrichmentDto<'IsochronesEnrichment'>) {
  const {
    avoidFeatureOptions,
    borderOption,
    geometryAttributeName,
    geometryObjecttypeName,
    isochroneType,
    routingMode,
    routingProfile,
    smoothing,
    zoneValue,
    locationType,
    time,
  } = enrichmentDto.parametrization;
  const repositoryName = getWorkbookRepositoryName(getStore().getState());
  const enrichedAttributeObjecttypeId = objecttypeIdToString(repositoryName, geometryObjecttypeName);
  const enrichedAttributeId = createAttributeId(enrichedAttributeObjecttypeId, geometryAttributeName);
  return {
    enrichedAttributeId,
    avoidFeatureOptions,
    borderOption,
    isochroneType,
    routingMode,
    routingProfile,
    smoothing,
    zoneValue,
    locationType,
    time,
  };
}

function createReverseGeocodingOptions(enrichmentDto: SpecificEnrichmentDto<'ReverseGeocodingEnrichment'>) {
  const enrichedObjecttypeId = enrichmentDto.enrichedObjecttypeId;
  const enrichedAttributeName = enrichmentDto.parametrization.enrichedAttributeName;
  return { enrichedAttributeId: createAttributeId(enrichedObjecttypeId, enrichedAttributeName) };
}

function createIpGeocodingOptions(enrichmentDto: SpecificEnrichmentDto<'IpGeocodingEnrichment'>) {
  const { ipAddressAttribute, ipAddressObjecttypeName } = enrichmentDto.parametrization;
  const repositoryName = getWorkbookRepositoryName(getStore().getState());
  const enrichedAttributeObjecttypeId = objecttypeIdToString(repositoryName, ipAddressObjecttypeName as ObjecttypeName);
  return {
    ipAddressAttribute: createAttributeId(enrichedAttributeObjecttypeId, ipAddressAttribute),
  };
}

function createAnalyticsExtensionOptions(enrichmentDto: SpecificEnrichmentDto<'AnalyticsExtensionEnrichment'>) {
  const parametrization = enrichmentDto.parametrization;
  return {
    analyticsExtensionId: parametrization.analyticsExtensionId,
    attributeGroupAssignments: parametrization.groupAssignments.map((assigment) => ({
      attributeGroupName: assigment.attributeGroupName,
      attributeIds: assigment.attributesAssignments.map((assigmentAttribute) =>
        createAttributeId(
          objecttypeIdToString(
            objecttypeIdFromString(enrichmentDto.enrichedObjecttypeId).repositoryName,
            assigmentAttribute.objecttypeName,
          ),
          assigmentAttribute.attributeName,
        ),
      ),
    })),
    parameters: Object.fromEntries(
      parametrization.parameters.map((analyticsExtensionParameterAssigment) => [
        analyticsExtensionParameterAssigment.key,
        analyticsExtensionParameterAssigment.serializedValue,
      ]),
    ),
  };
}

function createMeasureFromSpatialIntersectionOptions(
  enrichmentDto: SpecificEnrichmentDto<'MeasuresFromSpatialIntersectionEnrichment'>,
) {
  const { parametrization, repositoryName } = enrichmentDto;
  const enrichedAttributeId = createAttributeId(
    objecttypeIdToString(repositoryName, parametrization.enrichedObjecttypeName),
    parametrization.enrichedGeometryAttribute,
  );
  const providerObjecttypeId = objecttypeIdToString(repositoryName, parametrization.measureProvidingObjecttypeName);
  const providerAttributeId = createAttributeId(
    providerObjecttypeId,
    parametrization.measureProvidingGeometryAttribute,
  );
  return {
    enrichedAttributeId,
    providerAttribute: providerAttributeId,
    useBuffer: parametrization.useBuffer,
    bufferUnit: parametrization.bufferUnit,
    buffer: parametrization.buffer,
    calculations: parametrization.calculations.map((calculation) =>
      toMeasuresFromSpatialIntersectionCalculationDto(repositoryName, calculation),
    ),
  };
}

function toMeasuresFromSpatialIntersectionCalculationDto(
  repositoryName: RepositoryName,
  measureFromSpatialIntersectionCalculation: MeasuresFromSpatialIntersectionCalculation,
): MeasuresFromSpatialIntersectionCalculationDto {
  const measureProvidingMeasureObjecttypeId = objecttypeIdToString(
    repositoryName,
    measureFromSpatialIntersectionCalculation.measureProvidingMeasureObjecttypeName,
  );
  return {
    calculationName: measureFromSpatialIntersectionCalculation.calculationName,
    measureProvidingMeasureAttribute: createAttributeId(
      measureProvidingMeasureObjecttypeId,
      measureFromSpatialIntersectionCalculation.measureProvidingMeasureAttribute,
    ),
    aggregationFunction: MeasureAggregation.fromPersistenceKey(
      measureFromSpatialIntersectionCalculation.aggregationFunction,
    ),
    enrichmentAttributeName: measureFromSpatialIntersectionCalculation.enrichmentAttributeName,
    proportionalCalculation: measureFromSpatialIntersectionCalculation.proportionalCalculation,
  };
}
